import React, { useState, useEffect, useCallback, useMemo } from "react";
import DOMPurify from "dompurify";
import { getAvatarColor } from "./types";
import { formatFileSize, getFileTypeIcon } from "../utils/fileUtils";
import EmailPreview from "./EmailPreview";

interface EmailRecipient {
  contact_id: string;
  email: string;
  name: string;
}

interface EmailOpen {
  email_id: string;
  contact_id: string;
  contact_email: string;
  opened_at: string;
}

interface EmailHistoryItem {
  email_id: string;
  subject: string;
  html_body: string;
  sent_at: string;
  sent_by_email: string;
  recipient_count: number;
  recipients: EmailRecipient[];
  attachments?: Array<{
    filename: string;
    size: number;
    mimetype: string;
  }>;
  template_id?: string;
  template_name?: string;
}

interface EmailStats {
  totalOpens: number;
  uniqueOpens: number;
  opens: EmailOpen[];
}

interface EmailHistoryViewProps {
  accessToken: string;
  onEditDraft?: (draftId: string) => void;
  draftsSavedTrigger?: number; // Increment to trigger drafts refresh
  emailSentTrigger?: number; // Increment to trigger refresh after email sent
}

// Loading skeleton component
const LoadingSkeleton: React.FC = () => (
  <div className="flex flex-col h-full">
    <div className="flex-1 min-h-0 rounded bg-navy-deeper/50 border border-navy p-4">
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-2 border-rose-accent/30 border-t-rose-accent rounded-full animate-spin mb-3" />
          <p className="text-text-secondary text-sm">Loading email history...</p>
        </div>
      </div>
    </div>
  </div>
);

// Format date helper
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// File size formatting moved to shared utility (utils/fileUtils.ts)

// Email history item component
interface EmailHistoryItemProps {
  email: EmailHistoryItem;
  isExpanded: boolean;
  onToggle: () => void;
  stats: EmailStats | null;
  isLoadingStats: boolean;
}

const EmailHistoryItemComponent: React.FC<EmailHistoryItemProps> = ({
  email,
  isExpanded,
  onToggle,
  stats,
  isLoadingStats,
}) => {
  const [showRecipients, setShowRecipients] = useState(false);
  const [showOpens, setShowOpens] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);

  const openRate =
    stats && email.recipient_count > 0
      ? Math.round((stats.uniqueOpens / email.recipient_count) * 100)
      : 0;

  // Check if the email body is a full HTML document (template-based)
  // Use the stored template_id if available, otherwise check the HTML content
  const isTemplateEmail = useMemo(() => {
    // If we have a stored template_id, it's definitely a template email
    if (email.template_id) return true;

    // Fallback: check HTML content (handles comments at start)
    const htmlLower = email.html_body.toLowerCase().trim();
    // Check for DOCTYPE/HTML directly or after an HTML comment block
    return (
      htmlLower.startsWith("<!doctype") ||
      htmlLower.startsWith("<html") ||
      htmlLower.startsWith("<!--") && (htmlLower.includes("<!doctype") || htmlLower.includes("<html"))
    );
  }, [email.html_body, email.template_id]);

  // Sanitize HTML body for preview (only used for non-template emails)
  const sanitizedHtmlBody = useMemo(() => {
    if (isTemplateEmail) {
      // For template emails, return the full HTML for iframe rendering
      return email.html_body;
    }
    return DOMPurify.sanitize(email.html_body, {
      ALLOWED_TAGS: [
        "p",
        "br",
        "strong",
        "b",
        "em",
        "i",
        "u",
        "s",
        "strike",
        "a",
        "ul",
        "ol",
        "li",
        "h1",
        "h2",
        "h3",
        "span",
        "div",
      ],
      ALLOWED_ATTR: ["href", "target", "style", "class"],
    });
  }, [email.html_body, isTemplateEmail]);

  return (
    <div className="border-b border-navy/50 last:border-0">
      {/* Header - Always visible */}
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-4 p-4 hover:bg-navy/30 transition-colors text-left"
      >
        <div className="w-10 h-10 rounded bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white flex-shrink-0">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-white truncate">{email.subject}</p>
            {email.attachments && email.attachments.length > 0 && (
              <svg
                className="w-3.5 h-3.5 text-text-secondary flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                title={`${email.attachments.length} attachment${email.attachments.length !== 1 ? "s" : ""}`}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                />
              </svg>
            )}
          </div>
          <p className="text-xs text-text-muted">{formatDate(email.sent_at)}</p>
        </div>
        <div className="flex items-center gap-4 flex-shrink-0">
          <div className="text-right">
            <p className="text-sm text-text-primary">{email.recipient_count} recipients</p>
            {stats && (
              <p className="text-xs text-emerald-400">
                {stats.uniqueOpens} opened ({openRate}%)
              </p>
            )}
          </div>
          <svg
            className={`w-5 h-5 text-text-muted transition-transform ${isExpanded ? "rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-4">
          {/* Stats Cards */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-navy/50 rounded p-3 text-center">
              <p className="text-2xl font-bold text-white">{email.recipient_count}</p>
              <p className="text-xs text-text-muted">Recipients</p>
            </div>
            <div className="bg-navy/50 rounded p-3 text-center">
              {isLoadingStats ? (
                <div className="w-6 h-6 border-2 border-navy-light border-t-rose-accent rounded-full animate-spin mx-auto" />
              ) : (
                <>
                  <p className="text-2xl font-bold text-emerald-400">{stats?.uniqueOpens || 0}</p>
                  <p className="text-xs text-text-muted">Unique Opens</p>
                </>
              )}
            </div>
            <div className="bg-navy/50 rounded p-3 text-center">
              {isLoadingStats ? (
                <div className="w-6 h-6 border-2 border-navy-light border-t-rose-accent rounded-full animate-spin mx-auto" />
              ) : (
                <>
                  <p className="text-2xl font-bold text-rose-accent">{openRate}%</p>
                  <p className="text-xs text-text-muted">Open Rate</p>
                </>
              )}
            </div>
          </div>

          {/* Recipients Section */}
          <div className="rounded border border-border-strong overflow-hidden">
            <button
              onClick={() => setShowRecipients(!showRecipients)}
              className="w-full flex items-center justify-between px-4 py-3 bg-navy/30 hover:bg-navy/50 transition-colors"
            >
              <span className="text-sm text-text-primary">Recipients ({email.recipient_count})</span>
              <svg
                className={`w-4 h-4 text-text-muted transition-transform ${showRecipients ? "rotate-180" : ""}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            {showRecipients && (
              <div className="max-h-48 overflow-y-auto border-t border-border-strong">
                {email.recipients.map((recipient, idx) => {
                  const initials = recipient.name
                    ? recipient.name
                        .split(" ")
                        .map((n) => n[0])
                        .join("")
                        .toUpperCase()
                        .slice(0, 2)
                    : "?";
                  const avatarColor = getAvatarColor(recipient.name);
                  const hasOpened = stats?.opens.some((o) => o.contact_id === recipient.contact_id);

                  return (
                    <div
                      key={idx}
                      className="flex items-center gap-3 px-4 py-2.5 border-b border-border-subtle last:border-0"
                    >
                      <div
                        className={`flex-shrink-0 w-8 h-8 rounded-sm bg-gradient-to-br ${avatarColor} flex items-center justify-center text-white text-xs font-medium`}
                      >
                        {initials}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white truncate">{recipient.name || recipient.email}</p>
                        <p className="text-xs text-text-muted truncate">{recipient.email}</p>
                      </div>
                      {hasOpened && (
                        <span className="px-2 py-0.5 text-xs bg-emerald-500/20 text-emerald-400 rounded-full">
                          Opened
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Opens Section */}
          {stats && stats.opens.length > 0 && (
            <div className="rounded border border-border-strong overflow-hidden">
              <button
                onClick={() => setShowOpens(!showOpens)}
                className="w-full flex items-center justify-between px-4 py-3 bg-navy/30 hover:bg-navy/50 transition-colors"
              >
                <span className="text-sm text-text-primary">Open Events ({stats.totalOpens})</span>
                <svg
                  className={`w-4 h-4 text-text-muted transition-transform ${showOpens ? "rotate-180" : ""}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {showOpens && (
                <div className="max-h-48 overflow-y-auto border-t border-border-strong">
                  {stats.opens.map((open, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-3 px-4 py-2.5 border-b border-border-subtle last:border-0"
                    >
                      <svg
                        className="w-4 h-4 text-emerald-400 flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white truncate">{open.contact_email}</p>
                      </div>
                      <p className="text-xs text-text-muted flex-shrink-0">
                        {formatDate(open.opened_at)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Attachments Section */}
          {email.attachments && email.attachments.length > 0 && (
            <div className="rounded border border-border-strong overflow-hidden">
              <button
                onClick={() => setShowAttachments(!showAttachments)}
                className="w-full flex items-center justify-between px-4 py-3 bg-navy/30 hover:bg-navy/50 transition-colors"
              >
                <span className="text-sm text-text-primary flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                    />
                  </svg>
                  Attachments ({email.attachments.length})
                </span>
                <svg
                  className={`w-4 h-4 text-text-muted transition-transform ${showAttachments ? "rotate-180" : ""}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {showAttachments && (
                <div className="border-t border-border-strong">
                  {email.attachments.map((attachment, idx) => {
                    const fileIcon = getFileTypeIcon(attachment.mimetype);
                    return (
                      <div
                        key={idx}
                        className="flex items-center gap-3 px-4 py-3 border-b border-border-subtle last:border-0"
                      >
                        <svg
                          className="w-5 h-5 flex-shrink-0"
                          viewBox={fileIcon.viewBox}
                          fill={fileIcon.color}
                        >
                          <path d={fileIcon.path} />
                        </svg>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white truncate">{attachment.filename}</p>
                          <p className="text-xs text-text-muted">
                            {formatFileSize(attachment.size)} • {attachment.mimetype}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Email Preview */}
          <EmailPreview
            htmlContent={sanitizedHtmlBody}
            subject={email.subject}
            senderEmail={email.sent_by_email}
            recipientEmail={email.recipients[0]?.email || "recipient@example.com"}
            isTemplate={isTemplateEmail}
            templateName={email.template_name}
            bodyMaxHeight="256px"
          />
        </div>
      )}
    </div>
  );
};

const EmailHistoryView: React.FC<EmailHistoryViewProps> = ({
  accessToken,
  onEditDraft,
  draftsSavedTrigger,
  emailSentTrigger,
}) => {
  const [activeTab, setActiveTab] = useState<"sent" | "drafts">("sent");
  const [emails, setEmails] = useState<EmailHistoryItem[]>([]);
  const [drafts, setDrafts] = useState<EmailHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDraftsLoading, setIsDraftsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draftsError, setDraftsError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [emailStats, setEmailStats] = useState<Map<string, EmailStats>>(new Map());
  const [loadingStats, setLoadingStats] = useState<Set<string>>(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [draftToDelete, setDraftToDelete] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Fetch email history
  const fetchHistory = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch("/api/emails/history", {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to load email history");
      }

      const data = await response.json();
      // Handle both old array format and new paginated format
      setEmails(Array.isArray(data) ? data : data.emails || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  // Fetch drafts
  const fetchDrafts = useCallback(async () => {
    setIsDraftsLoading(true);
    setDraftsError(null);

    try {
      const response = await fetch("/api/emails/drafts", {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to load drafts");
      }

      const data = await response.json();
      setDrafts(data.drafts || []);
    } catch (err) {
      setDraftsError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsDraftsLoading(false);
    }
  }, [accessToken]);

  // Delete draft - shows confirmation modal
  const handleDeleteDraft = useCallback((draftId: string) => {
    setDraftToDelete(draftId);
    setShowDeleteConfirm(true);
    setDeleteError(null);
  }, []);

  // Confirm delete draft
  const confirmDeleteDraft = useCallback(async () => {
    if (!draftToDelete) return;

    try {
      const response = await fetch(`/api/emails/drafts/${draftToDelete}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to delete draft");
      }

      // Close modal and refresh drafts list
      setShowDeleteConfirm(false);
      setDraftToDelete(null);
      setDeleteError(null);
      await fetchDrafts();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : "Failed to delete draft");
    }
  }, [draftToDelete, accessToken, fetchDrafts]);

  // Fetch stats for a specific email
  const fetchStats = useCallback(
    async (emailId: string) => {
      if (emailStats.has(emailId) || loadingStats.has(emailId)) return;

      setLoadingStats((prev) => new Set(prev).add(emailId));

      try {
        const response = await fetch(`/api/emails/${emailId}/stats`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (response.ok) {
          const stats = await response.json();
          setEmailStats((prev) => new Map(prev).set(emailId, stats));
        }
      } catch (err) {
        console.error("Error fetching email stats:", err);
      } finally {
        setLoadingStats((prev) => {
          const newSet = new Set(prev);
          newSet.delete(emailId);
          return newSet;
        });
      }
    },
    [accessToken, emailStats, loadingStats]
  );

  // Initial fetch - fetch both sent emails and drafts to populate counts
  useEffect(() => {
    fetchHistory();
    fetchDrafts();
  }, [fetchHistory, fetchDrafts]);

  // Fetch drafts when drafts tab is selected or when draftsSavedTrigger changes
  useEffect(() => {
    if (activeTab === "drafts") {
      fetchDrafts();
    }
  }, [activeTab, fetchDrafts, draftsSavedTrigger]);

  // Fetch sent emails when sent tab is selected
  useEffect(() => {
    if (activeTab === "sent") {
      fetchHistory();
    }
  }, [activeTab, fetchHistory]);

  // Refresh both sent and drafts when an email is sent
  useEffect(() => {
    if (emailSentTrigger !== undefined && emailSentTrigger > 0) {
      fetchHistory();
      fetchDrafts();
    }
  }, [emailSentTrigger, fetchHistory, fetchDrafts]);

  // Fetch stats when email is expanded
  useEffect(() => {
    if (expandedId) {
      fetchStats(expandedId);
    }
  }, [expandedId, fetchStats]);

  const handleToggle = (emailId: string) => {
    setExpandedId((prev) => (prev === emailId ? null : emailId));
  };

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (error) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 min-h-0 rounded bg-navy-deeper/50 border border-red-900/50 p-4">
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <svg
                className="w-12 h-12 mx-auto text-red-500/50 mb-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <p className="text-red-400 text-sm mb-2">Failed to load email history</p>
              <p className="text-text-muted text-xs mb-4">{error}</p>
              <button
                onClick={fetchHistory}
                className="px-4 py-2 bg-rose-accent hover:bg-rose-bright text-navy-darkest text-sm rounded-sm transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Tab Navigation */}
      <div className="flex gap-2 pb-4 border-b border-border-strong mb-4">
        <button
          onClick={() => setActiveTab("sent")}
          className={`px-4 py-2 rounded-sm font-medium text-sm transition-colors ${
            activeTab === "sent"
              ? "bg-rose-dim text-rose-accent border border-rose-accent"
              : "bg-navy text-text-secondary hover:bg-navy-mid hover:text-text-primary"
          }`}
        >
          Sent ({emails.length})
        </button>
        <button
          onClick={() => setActiveTab("drafts")}
          className={`px-4 py-2 rounded-sm font-medium text-sm transition-colors ${
            activeTab === "drafts"
              ? "bg-rose-dim text-rose-accent border border-rose-accent"
              : "bg-navy text-text-secondary hover:bg-navy-mid hover:text-text-primary"
          }`}
        >
          Drafts ({drafts.length})
        </button>
      </div>

      {/* Sent Tab Content */}
      {activeTab === "sent" && (
        <>
          <div className="flex items-center justify-between pb-4">
            <p className="text-xs text-text-muted">
              {emails.length} email{emails.length !== 1 ? "s" : ""} sent
            </p>
            <button
              onClick={fetchHistory}
              className="text-xs text-rose-accent hover:text-rose-bright transition-colors flex items-center gap-1"
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Refresh
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto rounded bg-navy-deeper/50 border border-navy">
            {emails.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center py-12">
                  <svg
                    className="w-12 h-12 mx-auto text-navy-mid mb-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  <p className="text-text-muted text-sm">No emails sent yet</p>
                  <p className="text-navy-light text-xs mt-1">
                    Go to Contacts, select recipients, and send your first email
                  </p>
                </div>
              </div>
            ) : (
              emails.map((email) => (
                <EmailHistoryItemComponent
                  key={email.email_id}
                  email={email}
                  isExpanded={expandedId === email.email_id}
                  onToggle={() => handleToggle(email.email_id)}
                  stats={emailStats.get(email.email_id) || null}
                  isLoadingStats={loadingStats.has(email.email_id)}
                />
              ))
            )}
          </div>
        </>
      )}

      {/* Drafts Tab Content */}
      {activeTab === "drafts" && (
        <>
          <div className="flex items-center justify-between pb-4">
            <p className="text-xs text-text-muted">
              {drafts.length} draft{drafts.length !== 1 ? "s" : ""}
            </p>
            <button
              onClick={fetchDrafts}
              className="text-xs text-rose-accent hover:text-rose-bright transition-colors flex items-center gap-1"
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Refresh
            </button>
          </div>

          <div className="flex-1 min-h-0 rounded bg-navy-deeper/50 border border-navy overflow-y-auto p-4 space-y-3">
            {isDraftsLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="inline-block w-8 h-8 border-2 border-rose-accent/30 border-t-rose-accent rounded-full animate-spin mb-3" />
                  <p className="text-text-secondary text-sm">Loading drafts...</p>
                </div>
              </div>
            ) : draftsError ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <p className="text-red-400 text-sm mb-2">Failed to load drafts</p>
                  <p className="text-text-muted text-xs mb-4">{draftsError}</p>
                  <button
                    onClick={fetchDrafts}
                    className="px-4 py-2 bg-rose-accent hover:bg-rose-bright text-navy-darkest text-sm rounded-sm transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            ) : drafts.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <svg
                    className="w-16 h-16 mx-auto text-navy-mid mb-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <p className="text-text-muted text-sm">No drafts saved</p>
                  <p className="text-navy-light text-xs mt-1">
                    Save an email as draft while composing to find it here
                  </p>
                </div>
              </div>
            ) : (
              drafts.map((draft) => (
                <div
                  key={draft.email_id}
                  className="bg-navy/50 rounded-sm border border-border-subtle p-4 hover:border-border-strong transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-white truncate">
                        {draft.subject || "(No subject)"}
                      </h3>
                      <div className="flex items-center gap-3 mt-1 text-xs text-text-muted">
                        <span>
                          {draft.recipient_count} recipient{draft.recipient_count !== 1 ? "s" : ""}
                        </span>
                        {draft.updated_at && (
                          <>
                            <span>•</span>
                            <span>Last edited {formatDate(draft.updated_at)}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => onEditDraft?.(draft.email_id)}
                        className="px-3 py-1.5 text-xs font-medium text-rose-accent hover:text-rose-bright hover:bg-rose-glow rounded-sm transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteDraft(draft.email_id)}
                        className="px-3 py-1.5 text-xs font-medium text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-sm transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {/* Delete Draft Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="bg-navy rounded border border-border-strong max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded bg-red-500/20 flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-red-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Delete draft?</h3>
                <p className="text-sm text-text-secondary">This action cannot be undone.</p>
              </div>
            </div>

            {deleteError && (
              <div className="mb-4 p-3 rounded-sm bg-red-500/10 border border-red-500/20">
                <p className="text-sm text-red-400">{deleteError}</p>
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDraftToDelete(null);
                  setDeleteError(null);
                }}
                className="px-4 py-2 rounded-sm bg-navy-mid text-text-primary hover:bg-navy-light transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteDraft}
                className="px-4 py-2 rounded-sm bg-red-600 text-white hover:bg-red-500 transition-colors font-medium"
              >
                Delete Draft
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailHistoryView;
